import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { Reveal, LineReveal } from "@/components/Reveal";
import { Field, TextInput, TextArea, SubmitButton } from "@/components/forms";

export default function Pray() {
  const [form, setForm] = useState({ name: "", contact: "", request: "", confidential: false, allow_contact: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/prayer", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="How Can We Pray for You?"
        description="Request prayer from Victory Chapel Clay in Middleburg, FL - you don't have to attend. Every request is prayed over; private requests stay with our pastors."
        path="/prayer"
      />
      <section className="grain relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">Prayer</p>
          <LineReveal className="mt-4 font-display text-5xl font-black leading-[1.02] tracking-tighter sm:text-6xl lg:text-7xl" lines={["HOW CAN WE", "PRAY FOR YOU?"]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Whatever season you're in, you don't have to carry it alone. You don't have to attend
              Victory Chapel - or any church - to ask for prayer. Our team reads and prays over every
              request, and requests are never posted publicly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 sm:py-28" data-testid="prayer-section">
        <div className="mx-auto max-w-2xl px-5 sm:px-8">
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              className="rounded-3xl bg-navy p-10 text-center text-white shadow-lift" data-testid="prayer-success">
              <h2 className="font-display text-3xl font-800 tracking-tight">WE'RE PRAYING FOR YOU.</h2>
              <p className="mt-4 text-white/75">Your request has been received. Our team will be praying this week.</p>
            </motion.div>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="rounded-3xl bg-white p-6 shadow-lift sm:p-10" data-testid="prayer-form">
                <div className="space-y-5">
                  <Field label="First Name" id="pr-name" required>
                    <TextInput id="pr-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="given-name" />
                  </Field>
                  <Field label="Email or Phone" id="pr-contact" hint="Optional - only if you'd like us to follow up">
                    <TextInput id="pr-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
                  </Field>
                  <Field label="Prayer Request" id="pr-request" required>
                    <TextArea id="pr-request" rows={6} required value={form.request} onChange={(e) => setForm({ ...form, request: e.target.value })} />
                  </Field>
                  <label htmlFor="pr-allow-contact" className="flex cursor-pointer items-start gap-3 text-sm text-slate-600">
                    <input id="pr-allow-contact" type="checkbox" data-testid="prayer-contact-checkbox"
                           checked={form.allow_contact} onChange={(e) => setForm({ ...form, allow_contact: e.target.checked })}
                           className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#C8102E]" />
                    It's okay for VC Clay leadership to contact me about this request.
                  </label>
                  <label htmlFor="pr-confidential" className="flex cursor-pointer items-start gap-3 text-sm text-slate-600">
                    <input id="pr-confidential" type="checkbox" data-testid="prayer-confidential-checkbox"
                           checked={form.confidential} onChange={(e) => setForm({ ...form, confidential: e.target.checked })}
                           className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#C8102E]" />
                    Keep this request private.
                  </label>
                  <p className="text-xs leading-relaxed text-slate-500" data-testid="prayer-privacy-note">
                    Prayer requests are never displayed publicly. Requests marked private are shared
                    only with appropriate VC Clay leadership, who will pray over them personally.
                  </p>
                </div>
                <div className="mt-7">
                  <SubmitButton testId="prayer-submit" loading={loading}>SEND PRAYER REQUEST</SubmitButton>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
